import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  CardHeader
} from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import GavelIcon from '@mui/icons-material/Gavel';
import PrivacyTipIcon from '@mui/icons-material/PrivacyTip';

/**
 * Terms of Use Policy Page Component
 * Displays the terms and conditions for using Ualabi services
 */
const TermsOfUse = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Policy sections
  const sections = [
    {
      title: '1. Aceptación de los Términos',
      content: 'Al acceder o utilizar los servicios de Ualabi, usted acepta expresamente estos Términos de Uso y las políticas complementarias que se mencionan en el presente instrumento. Si no está de acuerdo, deberá abstenerse de utilizar los servicios.'
    },
    {
      title: '2. Definiciones',
      content: [
        '"Servicios": La plataforma de Ualabi (web, móvil) y su chatbot de WhatsApp.',
        '"Chatbot de WhatsApp": Canal oficial basado en WhatsApp Business bajo sus condiciones aplicables.',
        '"Contenido": Datos, mensajes o archivos enviados por el usuario o generados por la IA.',
        '"Datos Personales": Información que identifica o hace identificable a una persona, según Ley 19.628 de Chile.'
      ]
    },
    {
      title: '3. Descripción del Servicio',
      content: 'Ualabi permite interactuar con modelos de IA generativa para obtener respuestas y contenidos conversacionales. El servicio se ofrece "tal cual", pudiendo estar en fase Beta, sin garantías de disponibilidad continua.'
    },
    {
      title: '4. Elegibilidad y Registro',
      content: 'El usuario declara ser mayor de edad conforme a la legislación aplicable y proporcionar información veraz. Es responsable de mantener la confidencialidad de sus credenciales y de toda actividad realizada en su cuenta.'
    }
  ];

  // Featured sections with icons (security and privacy)
  const featuredSections = [
    {
      title: 'Fuentes de Información Confiables',
      content: 'Trabajamos con bases de datos de documentación técnica actualizada y no de fuentes libres para asegurar que nuestro modelo no genere información incorrecta o no verificada.',
      icon: <SecurityIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />
    },
    {
      title: 'Protección de Datos Personales',
      content: 'La información de los usuarios está protegida. Ualabi emplea la información recopilada exclusivamente para ofrecer respuestas personalizadas, mejorar la calidad del servicio y cumplir obligaciones legales.',
      icon: <PrivacyTipIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />
    },
    {
      title: 'Seguridad y Cifrado',
      content: 'Todos los mensajes están protegidos con cifrado avanzado, aplicando controles alineados con la "Ley Chilena 19629 | Sobre Protección de la Vida Privada" para proteger la confidencialidad, integridad y disponibilidad de la información.',
      icon: <GavelIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />
    }
  ];

  // Additional policy sections
  const additionalSections = [
    {
      title: '5. Seguridad, Cifrado y Privacidad de los Mensajes',
      content: [
        'Cifrado de extremo a extremo: Todos los mensajes que el usuario envíe o reciba a través del Chatbot de WhatsApp están protegidos con el protocolo Signal, lo que garantiza que solo el usuario y Ualabi puedan leerlos.',
        'Uso limitado de los datos: Ualabi empleará la información recopilada exclusivamente para (i) ofrecer respuestas personalizadas, (ii) mejorar la calidad del servicio y (iii) cumplir obligaciones legales; nunca publicará Datos Personales ni los venderá a terceros.',
        'Criptografía y controles internos: Ualabi aplica controles alineados con la "Ley Chilena 19629 | Sobre Protección de la Vida Privada"  para proteger la confidencialidad, integridad y disponibilidad de la información.'
      ]
    },
    {
      title: '6. Licencia de Uso y Propiedad Intelectual',
      content: 'Se concede al usuario una licencia limitada, no exclusiva y revocable para acceder a los Servicios bajo estos Términos. Todo derecho sobre la plataforma, modelos y marcas pertenece a Ualabi o sus licenciantes.'
    },
    {
      title: '7. Contenido del Usuario',
      content: 'El usuario conserva la titularidad de su Contenido, pero otorga a Ualabi una licencia mundial para alojarlo, procesarlo y mostrarlo con el fin de proveer y mejorar los Servicios. El usuario garantiza contar con los derechos necesarios y que su uso no infringe la ley ni derechos de terceros.'
    },
    {
      title: '8. Uso Permitido y Restricciones',
      content: [
        'El usuario se obliga a:',
        'Cumplir la ley, estos Términos y las Políticas de Uso Aceptable.',
        'Abstenerse de realizar actividades ilícitas, dañinas o abusivas, incluido el envío de spam o contenido prohibido por la Política de Mensajería de WhatsApp Business.',
        'Respetar límites de tasa y requisitos de opt-in establecidos por WhatsApp.'
      ]
    },
    {
      title: '9. Interacciones mediante WhatsApp',
      content: 'El uso del Chatbot de WhatsApp se rige adicionalmente por los WhatsApp Business Terms y la Business Messaging Policy; cualquier infracción puede implicar la suspensión del servicio.'
    },
    {
      title: '10. Exención de Responsabilidad sobre la Salida de IA',
      content: 'La información generada por la IA puede ser inexacta o incompleta; el usuario debe verificarla antes de tomar decisiones sustanciales. Los Servicios se proporcionan sin garantías de exactitud o adecuación para un propósito particular.'
    },
    {
      title: '11. Limitación de Responsabilidad',
      content: 'En la máxima medida permitida por la ley, Ualabi no será responsable de daños indirectos, incidentales o consecuentes derivados del uso o imposibilidad de uso de los Servicios.'
    },
    {
      title: '12. Suspensión y Terminación',
      content: 'Ualabi podrá suspender o cancelar una cuenta ante incumplimientos de estos Términos, de las Políticas de Uso o de las políticas de WhatsApp.'
    },
    {
      title: '13. Modificaciones',
      content: 'Ualabi podrá modificar estos Términos publicando una versión actualizada. Los cambios entrarán en vigor 30 días después de su publicación, salvo indicación contraria.'
    },
    {
      title: '14. Ley Aplicable y Jurisdicción',
      content: 'Estos Términos se regirán por las leyes de la República de Chile. Cualquier disputa se someterá a la jurisdicción exclusiva de los tribunales ordinarios de justicia de Santiago.'
    },
    {
      title: '15. Contacto',
      content: 'Para consultas sobre estos Términos o el tratamiento de datos personales, contacte a contacto@ualabi.co.'
    }
  ];

  // Component for rendering a section with title and content
  const Section = ({ title, content }) => (
    <Box mb={3}>
      <Typography variant="h6" component="h2" sx={{ fontWeight: 600, color: theme.palette.text.secondary, mb: 1 }}>
        {title}
      </Typography>
      {Array.isArray(content) ? (
        <List dense disablePadding>
          {content.map((item, idx) => (
            <ListItem key={idx} disableGutters sx={{ pl: 2 }}>
              <ListItemText
                primary={item}
                sx={{ color: theme.palette.text.primary }}
                primaryTypographyProps={{ variant: 'body2' }}
              />
            </ListItem>
          ))}
        </List>
      ) : (
        <Typography variant="body2" paragraph sx={{ color: theme.palette.text.primary }}>
          {content}
        </Typography>
      )}
    </Box>
  );

  return (
    <Box 
      sx={{ 
        py: 4,
        minHeight: 'calc(100vh - 116px)',
        bgcolor: theme.palette.background.default
      }}
    >
      <Container maxWidth="lg">
        <Typography 
          variant="h2" 
          component="h1" 
          sx={{ 
            mb: 3, 
            textAlign: 'center',
            color: theme.palette.text.secondary
          }}
        >
          Términos de Uso y Política de Privacidad
        </Typography>

        <Typography 
          variant="subtitle1" 
          sx={{ 
            mb: 4, 
            textAlign: 'center', 
            maxWidth: '800px', 
            mx: 'auto',
            color: theme.palette.text.primary 
          }}
        >
          Le recomendamos leer detenidamente estos Términos de Uso que definen cómo utilizamos su información y rigen su utilización de nuestros servicios.
        </Typography>

        {/* Featured sections with icons */}
        <Box 
          sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: 2,
            mb: 5,
            justifyContent: 'space-between'
          }}
        >
          {featuredSections.map((section, index) => (
            <Card 
              key={index} 
              elevation={2} 
              sx={{ 
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                mb: isMobile ? 2 : 0,
                borderLeft: `4px solid ${theme.palette.primary.main}`,
                borderRadius: 1
              }}
            >
              <CardHeader
                avatar={section.icon}
                title={section.title}
                titleTypographyProps={{ variant: 'h6', sx: { fontWeight: 600 } }}
              />
              <CardContent sx={{ pt: 0 }}>
                <Typography variant="body2" color="text.primary">
                  {section.content}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>

        {/* Main policy document */}
        <Paper 
          elevation={2} 
          sx={{ 
            p: 4, 
            borderRadius: 2,
            mb: 4
          }}
        >
          <Typography 
            variant="h5" 
            sx={{ 
              mb: 3, 
              fontWeight: 600, 
              color: theme.palette.primary.main 
            }}
          >
            Términos Legales
          </Typography>
          
          {/* Initial sections */}
          {sections.map((section, index) => (
            <React.Fragment key={index}>
              <Section title={section.title} content={section.content} />
              {index < sections.length - 1 && <Divider sx={{ my: 2 }} />}
            </React.Fragment>
          ))}
          
          <Divider sx={{ my: 4 }} />
          
          {/* Additional sections */}
          {additionalSections.map((section, index) => (
            <React.Fragment key={index}>
              <Section title={section.title} content={section.content} />
              {index < additionalSections.length - 1 && <Divider sx={{ my: 2 }} />}
            </React.Fragment>
          ))}
        </Paper>

        <Box sx={{ textAlign: 'center', mt: 4, mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Última actualización: Mayo 20, 2025
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default TermsOfUse;
